import React, { useState } from 'react'
import Dashboard from '../../services/Dashboard'
import {
  ageRows, formatDecimal, formatNumber, formatPercent, formatShare, includesSystemStart, pacientesFilterLabels, percentOf, placeRows,
  placesFootnotes, plural, registrationRows, resolvePacientesParams, sexRows, todayString,
} from '../../utilities/dashboard'
import { BlockError, KpiSkeleton } from './BlockState'
import AgeChart from './charts/AgeChart'
import ChartCard, { DataTable } from './charts/ChartCard'
import RankingChart from './charts/RankingChart'
import RegistrationsChart from './charts/RegistrationsChart'
import SexChart from './charts/SexChart'
import useChartColors from './charts/useChartColors'
import { useDashboardBlock, useIsNarrow } from './hooks'
import KpiCard from './KpiCard'
import PacientesFilters from './PacientesFilters'
import PacientesListModal from './PacientesListModal'

const SEX_NOTE = 'O cadastro de paciente já abre com MASCULINO marcado: quem não trocou ficou como masculino. Por isso a parte masculina aparece maior do que a real.'
const SYSTEM_START_NOTE = 'O sistema começou em jul/2024: os cadastros desse mês incluem quem já era paciente.'

const placeTooltip = (row) => ({ title: row.label, rows: [{ value: formatNumber(row.value), label: row.value === 1 ? 'paciente' : 'pacientes' }] })

// Aba Pacientes: a base de cadastros, com os filtros de situação, atendimento nos últimos 90 dias e data de cadastro
const TabPacientes = ({ params, onParamsChange, onForbidden }) => {
  const chartPalette = useChartColors()
  const narrow = useIsNarrow()
  const today = todayString()
  const filters = resolvePacientesParams(params, today)
  const requestKey = `${filters.ativo}|${filters.attendance}|${filters.start_date}|${filters.end_date}`
  const summary = useDashboardBlock(requestKey, () => Dashboard.findPacientesSummary(filters), onForbidden)
  const [list, setList] = useState({ type: null, show: false })

  const data = summary.data
  const stale = summary.loading && Boolean(data)
  const chartState = { skeleton: !data, error: summary.error, onRetry: summary.reload, stale }
  const total = data?.total_pacientes ?? 0
  const age = data?.age
  const address = data?.address
  const filterText = pacientesFilterLabels(filters, today).join(' · ')
  const registrations = registrationRows(data?.registrations, data?.today || today)
  const ages = ageRows(age?.ranges, chartPalette)
  const sexes = sexRows(data?.sex, chartPalette)
  const cities = placeRows(address?.cities, 'city')
  const neighborhoods = placeRows(address?.neighborhoods, 'neighborhood')
  const openList = (type) => setList({ type, show: true })

  return (
    <div className='dashboard-section'>
      <PacientesFilters filters={filters} today={today} onChange={onParamsChange} />

      {summary.error ?
        <BlockError message={summary.error} onRetry={summary.reload} />
        :
        <div className={`dashboard-kpis ${stale ? 'dashboard-stale' : ''}`}>
          {!data ? <KpiSkeleton /> :
            <>
              <KpiCard label='Pacientes' info='pacientes_total' value={formatNumber(total)} sub={`${plural(data.total_active, 'ativo', 'ativos')} · ${plural(data.total_inactive, 'inativo', 'inativos')}`} />
              <KpiCard
                label={`Atendidos nos últimos ${data.recent_days ?? 90} dias`}
                info='pacientes_recent'
                value={formatNumber(data.total_recent)}
                sub={total ? `${formatShare(percentOf(data.total_recent, total))} dos pacientes do filtro` : 'nenhum paciente no filtro'}
              />
              <KpiCard
                label='Idade média'
                info='pacientes_age'
                value={(age?.average === null || age?.average === undefined) ? '—' : `${formatDecimal(age.average)} anos`}
                sub={age?.total_with_birth_date ? `de ${plural(age.total_with_birth_date, 'paciente', 'pacientes')} com data de nascimento` : 'sem data de nascimento'}
              />
              <KpiCard
                label='Sem prontuário'
                info='pacientes_without_prontuario'
                value={formatNumber(data.without_prontuario?.total)}
                sub={`${formatNumber(data.without_prontuario?.with_done_session)} já com sessão realizada`}
                action={data.without_prontuario?.total ? <button type='button' className='dashboard-link-button align-self-start' onClick={() => openList('WITHOUT_PRONTUARIO')}>Ver lista</button> : null}
              />
              <KpiCard
                label='Sem contrato'
                info='pacientes_without_contrato'
                value={formatNumber(data.without_contrato?.total)}
                sub={`${formatNumber(data.without_contrato?.recent)} atendidos nos últimos ${data.recent_days ?? 90} dias`}
                action={data.without_contrato?.total ? <button type='button' className='dashboard-link-button align-self-start' onClick={() => openList('WITHOUT_CONTRATO')}>Ver lista</button> : null}
              />
              <KpiCard
                label='Com endereço'
                info='pacientes_address'
                value={formatNumber(address?.total_with_city)}
                sub={total ? `${formatShare(percentOf(address?.total_with_city ?? 0, total))} · ${plural(address?.total_with_neighborhood ?? 0, 'com bairro', 'com bairro')}` : 'cidade preenchida no cadastro'}
              />
            </>
          }
        </div>
      }

      {/* Todos os blocos vêm da mesma chamada: com erro, o aviso aparece uma vez só, acima */}
      {summary.error ? '' : (data && !total) ?
        <p className='dashboard-empty dashboard-card'>Nenhum paciente neste filtro.</p>
        :
        <div className='dashboard-grid'>
          <ChartCard
            wide
            title='Cadastros por mês'
            info='pacientes_registrations'
            subtitle={filterText}
            {...chartState}
            empty={!registrations.some((row) => row.total)}
            emptyText='Nenhum cadastro no período.'
            renderTable={() => (
              <DataTable head={['Mês', 'Cadastros']} rows={registrations.map((row) => [`${row.title}${row.partialTag ? ' (parcial)' : ''}`, formatNumber(row.total)])} />
            )}
            footer={includesSystemStart(data?.registrations) ? <p className='dashboard-card-note mb-0'>{SYSTEM_START_NOTE}</p> : null}
          >
            <RegistrationsChart rows={registrations} narrow={narrow} title={`Cadastros por mês — ${filterText}`} />
          </ChartCard>

          <ChartCard
            title='Faixa etária'
            info='pacientes_age_ranges'
            subtitle={age?.total_with_birth_date
              ? `idade média ${formatDecimal(age.average)} anos · ${plural(age.total_without_birth_date, 'paciente sem data de nascimento válida fica fora', 'pacientes sem data de nascimento válida ficam fora')}`
              : 'idade na data de hoje'}
            {...chartState}
            empty={!age?.total_with_birth_date}
            emptyText='Nenhum paciente do filtro tem data de nascimento.'
            renderTable={() => (
              <DataTable
                head={['Faixa', 'Pacientes', '%']}
                rows={ages.map((row) => [`${row.label} anos`, formatNumber(row.total), formatPercent(percentOf(row.total, age.total_with_birth_date))])}
              />
            )}
          >
            <AgeChart rows={ages} totalWithBirth={age?.total_with_birth_date} narrow={narrow} title={`Faixa etária — ${filterText}`} />
          </ChartCard>

          <ChartCard
            title='Sexo'
            info='pacientes_sex'
            subtitle='como está no cadastro do paciente'
            {...chartState}
            empty={!total}
            emptyText='Nenhum paciente neste filtro.'
            renderTable={() => <DataTable head={['Sexo', 'Pacientes', '%']} rows={sexes.map((row) => [row.label, formatNumber(row.total), row.percent === null ? '—' : formatPercent(row.percent)])} />}
            footer={<p className='dashboard-card-note dashboard-card-note-warning mb-0'>{SEX_NOTE}</p>}
          >
            <SexChart rows={sexes} total={total} />
          </ChartCard>

          <ChartCard
            title='Cidades'
            info='pacientes_cities'
            subtitle={`as ${Math.min(cities.length, 10) || 10} cidades com mais pacientes`}
            {...chartState}
            empty={!cities.length}
            emptyText='Nenhum paciente do filtro tem cidade no cadastro.'
            renderTable={() => <DataTable head={['Cidade', 'Pacientes']} rows={cities.map((row) => [row.label, formatNumber(row.value)])} />}
            footer={placesFootnotes(address, total, 'city').map((note) => <p className='dashboard-card-note mb-0' key={note}>{note}</p>)}
          >
            <RankingChart rows={cities} tooltipFor={placeTooltip} labelWidth={150} valueWidth={48} />
          </ChartCard>

          <ChartCard
            title='Bairros'
            info='pacientes_neighborhoods'
            subtitle={`os ${Math.min(neighborhoods.length, 10) || 10} bairros com mais pacientes (bairro · cidade)`}
            {...chartState}
            empty={!neighborhoods.length}
            emptyText='Nenhum paciente do filtro tem bairro no cadastro.'
            renderTable={() => (
              <DataTable
                head={['Bairro', 'Cidade', 'Pacientes']}
                rows={(address?.neighborhoods || []).map((row) => [row.bairro, row.cidade || '—', formatNumber(row.total)])}
              />
            )}
            footer={placesFootnotes(address, total, 'neighborhood').map((note) => <p className='dashboard-card-note mb-0' key={note}>{note}</p>)}
          >
            <RankingChart rows={neighborhoods} tooltipFor={placeTooltip} labelWidth={200} valueWidth={48} />
          </ChartCard>
        </div>
      }

      {list.type ?
        <PacientesListModal
          key={list.type}
          listType={list.type}
          show={list.show}
          filters={filters}
          onHide={() => setList((current) => ({ ...current, show: false }))}
          onForbidden={onForbidden}
        />
        : ''}
    </div>
  )
}

export default TabPacientes
